import { useState } from "react";
import { LEAGUES, MAX_NEWS_SOURCES, MAX_TEAMS, NEWS_CATEGORIES, NEWS_SOURCES, sourceById, type League, type NewsSettings } from "../../../shared/news";
import { useHousehold } from "../../app/useHousehold";
import { useLeagueTeams } from "../../data/newsQueries";
import { useUpdateSettings } from "../../data/queries";
import { Icon } from "../../ui/Icon";

/** The News screen: which sources it reads, which ones lead the top story, and the family's sports. */
export function NewsSection() {
  const { settings, isAdmin } = useHousehold();
  const update = useUpdateSettings();
  const [msg, setMsg] = useState<string | null>(null);
  const [league, setLeague] = useState<League>("nfl");
  const [team, setTeam] = useState("");
  const teams = useLeagueTeams(isAdmin ? league : null);
  const n = settings.news;
  const save = (patch: Partial<NewsSettings>) => update.mutate({ news: { ...n, ...patch } }, { onSuccess: () => setMsg(null), onError: (e) => setMsg((e as Error).message) });

  const toggleSource = (id: string) => {
    const on = n.sources.includes(id);
    save(on ? { sources: n.sources.filter((x) => x !== id), hero: n.hero.filter((x) => x !== id) } : { sources: [...n.sources, id] });
  };
  const toggleHero = (id: string) => save({ hero: n.hero.includes(id) ? n.hero.filter((x) => x !== id) : [...n.hero, id] });
  const addTeam = () => {
    if (!team || n.teams.some((t) => t.league === league && t.team === team)) return;
    save({ teams: [...n.teams, { league, team }] });
    setTeam("");
  };
  const teamName = (l: League, id: string) => (l === league ? teams.data?.find((t) => t.abbr.toLowerCase() === id || t.id === id)?.name : null) ?? `${LEAGUES[l].name} · ${id.toUpperCase()}`;

  return (
    <div className="form">
      <h3>News & sports</h3>
      <p className="muted">The News screen: a rotating top story with pictures, headlines from the sources you turn on, Formula 1 and your teams. Tap any story to read it right there; a story that can’t be shown (a paywall, a video) offers a QR code to read it on your phone.</p>

      <h4>Sources</h4>
      <p className="muted small">
        Up to {MAX_NEWS_SOURCES}. {n.sources.length} on.
      </p>
      {NEWS_CATEGORIES.map((c) => (
        <div key={c.key} className="field">
          <span>{c.label}</span>
          <div className="chip-row wrap" role="group" aria-label={c.label}>
            {NEWS_SOURCES.filter((s) => s.category === c.key).map((s) => {
              const on = n.sources.includes(s.id);
              return (
                <button
                  key={s.id}
                  className={`chip ${on ? "on" : ""}`}
                  aria-pressed={on}
                  disabled={!isAdmin || update.isPending || (!on && n.sources.length >= MAX_NEWS_SOURCES)}
                  onClick={() => toggleSource(s.id)}
                >
                  {on && <Icon name="check" size="1rem" />}
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <hr />
      <h4>Top story</h4>
      <p className="muted small">The big picture story takes turns between these sources.</p>
      {n.sources.length === 0 ? (
        <p className="muted small">Turn on a source first.</p>
      ) : (
        <div className="chip-row wrap" role="group" aria-label="Top story sources">
          {n.sources.map((id) => {
            const on = n.hero.includes(id);
            return (
              <button key={id} className={`chip ${on ? "on" : ""}`} aria-pressed={on} disabled={!isAdmin || update.isPending || (!on && n.hero.length >= 6)} onClick={() => toggleHero(id)}>
                {on && <Icon name="star" size="1rem" />}
                {sourceById(id)?.name ?? id}
              </button>
            );
          })}
        </div>
      )}
      <div className="field">
        <span>Each story stays up</span>
        <div className="chip-row">
          {[8, 12, 20, 30].map((s) => (
            <button key={s} className={`chip ${n.heroSeconds === s ? "on" : ""}`} disabled={!isAdmin || update.isPending} onClick={() => save({ heroSeconds: s })}>
              {s} s
            </button>
          ))}
        </div>
      </div>

      <hr />
      <h4>Sports</h4>
      <label className="field field-toggle">
        <span>
          Formula 1
          <small className="muted">The next Grand Prix with its track, the championship standings and the last podium.</small>
        </span>
        <input type="checkbox" className="switch" checked={n.f1} disabled={!isAdmin || update.isPending} onChange={(e) => save({ f1: e.target.checked })} />
      </label>
      <div className="field">
        <span>Teams</span>
        {n.teams.length === 0 ? (
          <p className="muted small">No teams yet. The Team card shows team 1; add another Team card for each of the others.</p>
        ) : (
          <ul className="spot-admin">
            {n.teams.map((t, i) => (
              <li key={`${t.league}:${t.team}`}>
                <Icon name="trophy" size="1.1rem" />
                <strong>
                  {i + 1}. {teamName(t.league, t.team)}
                </strong>
                <span className="muted small">{LEAGUES[t.league].name}</span>
                {isAdmin && (
                  <button className="icon-btn" onClick={() => save({ teams: n.teams.filter((_, j) => j !== i) })} aria-label="Remove team">
                    <Icon name="trash" size="1.1rem" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {isAdmin && n.teams.length < MAX_TEAMS && (
          <div className="row">
            <select className="input" value={league} onChange={(e) => (setLeague(e.target.value as League), setTeam(""))} aria-label="League">
              {(Object.keys(LEAGUES) as League[]).map((l) => (
                <option key={l} value={l}>
                  {LEAGUES[l].name}
                </option>
              ))}
            </select>
            <select className="input" value={team} onChange={(e) => setTeam(e.target.value)} aria-label="Team" disabled={!teams.data}>
              <option value="">{teams.isLoading ? "Loading teams…" : teams.isError ? "Teams unavailable" : "Choose a team"}</option>
              {(teams.data ?? []).map((t) => (
                <option key={t.id} value={t.abbr.toLowerCase()}>
                  {t.name}
                </option>
              ))}
            </select>
            <button className="btn" onClick={addTeam} disabled={!team || update.isPending}>
              Add team
            </button>
          </div>
        )}
      </div>
      {msg && <p className="error-text">{msg}</p>}
    </div>
  );
}
